import type { TextStyle, ShapeStyle, ConnectorStyle } from '../diagram/state/types';
import { SHAPE_PRESETS, SHAPE_PRESETS_DARK, THEME_COLOR_MAP } from '../diagram/state/types';
import { swapBlackWhite, swapTextColor, swapThemeColor } from '../../utils/color';
import { ViewportControllerBase, type ViewportContextBase } from '../../utils/ViewportControllerBase';
import type { UnifiedStore } from './UnifiedStore';

interface UnifiedViewportContext extends ViewportContextBase {
  store: UnifiedStore;
  getCurrentTextStyle: () => TextStyle;
  getCurrentStyle: () => Partial<ShapeStyle>;
  setCurrentStyle: (style: Partial<ShapeStyle>) => void;
  getCurrentConnectorStyle: () => Partial<ConnectorStyle>;
  setCurrentConnectorStyle: (style: Partial<ConnectorStyle>) => void;
}

export class UnifiedViewportController extends ViewportControllerBase<UnifiedViewportContext> {
  constructor(context: UnifiedViewportContext, initialState?: { gridEnabled?: boolean; snapEnabled?: boolean }) {
    super(context, initialState);
  }

  resetView(bounds: { minX: number; minY: number; maxX: number; maxY: number } | null, rect: DOMRect): void {
    this.scale = 1;

    if (bounds) {
      const centerX = (bounds.minX + bounds.maxX) / 2;
      const centerY = (bounds.minY + bounds.maxY) / 2;
      this.panX = rect.width / 2 - centerX * this.scale;
      this.panY = rect.height / 2 - centerY * this.scale;
    } else {
      this.panX = 0;
      this.panY = 0;
    }

    this.context.render();
    this.notifyViewChange();
  }

  fitToScreen(bounds: { minX: number; minY: number; maxX: number; maxY: number } | null, rect: DOMRect): void {
    if (!bounds) {
      this.resetView(null, rect);
      return;
    }

    const padding = 50;
    const contentWidth = bounds.maxX - bounds.minX + padding * 2;
    const contentHeight = bounds.maxY - bounds.minY + padding * 2;

    const scaleX = rect.width / contentWidth;
    const scaleY = rect.height / contentHeight;
    this.scale = Math.min(scaleX, scaleY, 1);

    const centerX = (bounds.minX + bounds.maxX) / 2;
    const centerY = (bounds.minY + bounds.maxY) / 2;
    this.panX = rect.width / 2 - centerX * this.scale;
    this.panY = rect.height / 2 - centerY * this.scale;

    this.context.render();
    this.notifyViewChange();
  }

  setInverted(enabled: boolean): void {
    if (this.inverted === enabled) return;
    const themeMap = THEME_COLOR_MAP;
    const store = this.context.store;

    // --- Draw-mode elements ---
    for (const stroke of store.strokes) {
      stroke.color = swapBlackWhite(stroke.color);
    }
    for (const drawShape of store.drawShapes) {
      drawShape.style.stroke = swapBlackWhite(drawShape.style.stroke);
    }

    // --- Diagram-mode elements ---
    for (const shape of store.shapes) {
      shape.style.fill = swapThemeColor(shape.style.fill, themeMap);
      shape.style.stroke = swapThemeColor(shape.style.stroke, themeMap);
      shape.textStyle.color = shape.style.stroke;
    }
    for (const connector of store.connectors) {
      connector.style.stroke = swapThemeColor(connector.style.stroke, themeMap);
    }

    // --- Shared text elements ---
    for (const textElement of store.textElements) {
      textElement.textStyle.color = swapTextColor(textElement.textStyle.color, themeMap);
    }

    // --- Current drawing text style ---
    const currentTextStyle = this.context.getCurrentTextStyle();
    const swappedColor = swapBlackWhite(currentTextStyle.color);
    if (swappedColor !== currentTextStyle.color) {
      currentTextStyle.color = swappedColor;
    }

    // --- Current diagram shape style presets ---
    const presets = enabled ? SHAPE_PRESETS_DARK : SHAPE_PRESETS;
    const currentStyle = this.context.getCurrentStyle();
    this.context.setCurrentStyle({
      ...currentStyle,
      fill: presets['default']?.fill ?? (enabled ? '#374151' : '#ffffff'),
      stroke: presets['default']?.stroke ?? (enabled ? '#9ca3af' : '#374151'),
    });
    const currentConnectorStyle = this.context.getCurrentConnectorStyle();
    this.context.setCurrentConnectorStyle({
      ...currentConnectorStyle,
      stroke: enabled ? '#9ca3af' : '#1a1a1a',
    });

    this.inverted = enabled;
    this.context.render();
    this.notifyToggleChange();
    this.context.notifyChange();
  }
}
